<template>
    <div>
        <div class="w-full md:flex justify-between items-center">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="sm:col-span-1">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.sort_order') }}
                    </label>
                    <div class="mt-1">
                        <label v-for="sortOption in sortOptions" :key="sortOption.value">
                            <input
                                type="radio"
                                v-model="sort"
                                :value="sortOption.value"
                                name="sortOrder"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            {{ sortOption.name }}
                        </label>
                    </div>
                </div>

                <div class="sm:col-span-1">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.filter') }}
                    </label>
                    <div class="mt-1">
                        <input
                            type="search"
                            v-model="search"
                            tabindex="1"
                            class="bg-indigo-50 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                        />
                    </div>
                </div>

                <div class="sm:col-span-1" v-show="absent.length">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.show')" for="show" />
                        <FormSelect
                            id="grouping"
                            v-model="show"
                            :options="showOptions"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                    </div>
                </div>
            </div>

            <div class="flex justify-between items-center space-x-2" v-show="scoredStudents.length === 0">
                <button
                    type="button"
                    @click="showTool = !showTool"
                    class="mr-1 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    {{ $t('messages.import_grades_for_the_group') }}
                </button>

                <div :title="$t('messages.paste_scores_grading_tool')">
                    <svg
                        class="w-6 h-6 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                    </svg>
                </div>
            </div>
        </div>

        <div class="w-full mt-3 border-8 border-indigo-300" v-show="showTool && scoredStudents.length === 0">
            <div class="border border-danger px-4 py-2">
                <label for="scoresInline">{{ $t('messages.enter_scores_separated_by_spaces') }}</label>
                <div class="w-full">
                    <div class="mt-1">
                        <input
                            type="text"
                            class="w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            id="scoresInline"
                            v-model="scoresInline"
                            placeholder="5 4 6 4 2 10 8 7 5 6 7 8 10"
                        />
                    </div>
                    <div class="mt-1">
                        <button
                            type="button"
                            class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            :disabled="scoresFromLine.length !== totalStudents"
                            @click="assignScores"
                            :title="
                                scoresFromLine.length === totalStudents
                                    ? ''
                                    : $t('messages.the_number_of_scores_doesnt_match_number_of_students')
                            "
                        >
                            {{ $t('messages.add_scores') }}
                        </button>
                    </div>
                </div>
                <div>
                    {{ $t('messages.paste_scores_grading_tool_requirements') }}
                    <span> {{ scoresFromLine.length }}/ {{ totalStudents }} </span>
                </div>
            </div>
        </div>

        <div class="hidden md:flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.score') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    v-if="activity.max_score !== 10"
                                >
                                    d10
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.adapted') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.absent') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.signed') }}
                                    ({{ signedStudents.length }}/{{ totalStudents }})
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!--                        v-show="searchedUserIds.includes(activityResponse.student_id)"-->
                            <tr
                                v-for="(activityResponse, index) in filteredResponses"
                                :key="activityResponse.student_id"
                                class="hover:bg-gray-200"
                                :class="{
                                    'opacity-50': shouldDistinguish(activityResponse.student_id),
                                }"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ activityResponse.student.full_name }}
                                </td>
                                <td
                                    class="flex items-center px-4 sm:px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            v-model="activityResponse.score"
                                            type="number"
                                            @blur="
                                                activityResponse.absent =
                                                    activityResponse.score === null || activityResponse.score === ''
                                            "
                                            :name="'score[' + activityResponse.student_id + ']'"
                                            step="0.01"
                                            :max="activity.max_score"
                                            min="0"
                                            :tabindex="index + 1 + 1"
                                            class="disable-input-arrows max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                                            placeholder=""
                                            style="min-width: 70px"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <span class="hidden xl:inline-block">/ {{ activity.max_score }}</span>
                                        </div>
                                    </div>
                                    <div class="block xl:hidden ml-3">/ {{ activity.max_score }}</div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    v-if="activity.max_score !== 10"
                                >
                                    {{ translateToDecimal(activityResponse.score) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <input
                                        type="text"
                                        :list="`datalist_comment_${activityResponse.student_id}`"
                                        v-model="activityResponse.comment"
                                        maxlength="180"
                                        :name="'comment[' + activityResponse.student_id + ']'"
                                        class="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                                    />
                                    <datalist :id="`datalist_comment_${activityResponse.student_id}`">
                                        <template
                                            v-if="
                                                activityResponse.student.hasOwnProperty('current_student_file') &&
                                                activityResponse.student.current_student_file.tags.length
                                            "
                                        >
                                            <option
                                                :value="tag.display_name"
                                                :key="tag.display_name"
                                                v-for="tag in activityResponse.student.current_student_file.tags"
                                            ></option>
                                        </template>
                                    </datalist>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <label>
                                        <svg
                                            class="w-4 h-4 focus:ring-indigo-500"
                                            :class="activityResponse.adapted ? 'text-indigo-600' : 'text-gray-400'"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                        <input
                                            v-model="activityResponse.adapted"
                                            type="checkbox"
                                            name="adapted[]"
                                            :value="activityResponse.student_id"
                                            class="hidden focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                    </label>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <input
                                        v-model="activityResponse.absent"
                                        name="absent[]"
                                        :disabled="!(activityResponse.score === null || activityResponse.score === '')"
                                        :value="activityResponse.student_id"
                                        type="checkbox"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <input
                                        v-model="activityResponse.signed"
                                        type="checkbox"
                                        name="signed[]"
                                        :value="activityResponse.student_id"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div class="block md:hidden">
            <div
                v-for="(activityResponse, index) in activityResponses"
                :key="index"
                class="mt-6"
                :class="index % 2 === 0 ? 'bg-gray-100' : ''"
                v-show="searchedUserIds.includes(activityResponse.student_id)"
            >
                <div class="grid grid-cols-6 gap-y-6 gap-x-4 py-2">
                    <div class="col-span-6">
                        {{ activityResponse.student.full_name }}
                    </div>
                    <div class="col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.score') }} ({{ translateToDecimal(activityResponse.score) }}
                            / 10)
                        </label>
                        <div class="mt-1">
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <input
                                    v-model="activityResponse.score"
                                    type="number"
                                    @blur="
                                        activityResponse.absent =
                                            activityResponse.score === null || activityResponse.score === ''
                                    "
                                    :name="'score[' + activityResponse.student_id + ']'"
                                    step="0.01"
                                    :max="activity.max_score"
                                    min="0"
                                    :tabindex="index + 1 + 1 + totalStudents"
                                    class="disable-input-arrows max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                                    placeholder=""
                                    style="min-width: 70px"
                                />
                                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <span class="inline-block">/ {{ activity.max_score }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.adapted') }}
                        </label>
                        <div class="mt-1">
                            <label>
                                <svg
                                    class="w-4 h-4 focus:ring-indigo-500"
                                    :class="activityResponse.adapted ? 'text-indigo-600' : 'text-gray-400'"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                                <input
                                    v-model="activityResponse.adapted"
                                    type="checkbox"
                                    name="adapted[]"
                                    :value="activityResponse.student_id"
                                    class="hidden focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </label>
                        </div>
                    </div>
                    <div class="col-span-3" style="display: none">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> d/10 </label>
                        <div class="mt-1">
                            {{ translateToDecimal(activityResponse.score) }}
                        </div>
                    </div>
                    <div class="col-span-6">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.comment') }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="text"
                                :list="`datalist_comment_${activityResponse.student_id}`"
                                v-model="activityResponse.comment"
                                :name="'comment[' + activityResponse.student_id + ']'"
                                class="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>
                    </div>
                    <div class="col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.absent') }}
                        </label>
                        <div class="mt-1">
                            <input
                                v-model="activityResponse.absent"
                                name="absent[]"
                                :disabled="!(activityResponse.score === null || activityResponse.score === '')"
                                :value="activityResponse.student_id"
                                type="checkbox"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                        </div>
                    </div>
                    <div class="col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.signed') }}
                        </label>
                        <div class="mt-1">
                            <input
                                v-model="activityResponse.signed"
                                type="checkbox"
                                name="signed[]"
                                :value="activityResponse.student_id"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ActivityGradesSummary :values="decimalScores" :ranges="ranges" />

        <ActivityGradeResponsesGraphs
            :bar-chart-options="barChartOptions"
            :line-chart-options="lineChartOptions"
            :values="decimalScores"
            :ranges="ranges"
        />
    </div>
</template>

<script>
import GradeTranslator from '../../classes/GradeTranslator';
import deburr from 'lodash/deburr';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import ActivityGradeResponsesGraphs from '@/components/pageSpecific/ActivityGradeResponsesGraphs.vue';
import ActivityGradesSummary from '@/components/pageSpecific/ActivityGradesSummary.vue';

export default {
    components: {
        FormLabel,
        FormSelect,
        ActivityGradeResponsesGraphs,
        ActivityGradesSummary,
    },
    data() {
        return {
            show: 'all',
            showOptions: [
                {
                    value: 'all',
                    text: this.$t('messages.all'),
                },
                {
                    value: 'distinguish_absent_students',
                    text: this.$t('messages.distinguish_absent_students'),
                },
                {
                    value: 'hide_absent_students',
                    text: this.$t('messages.hide_absent_students'),
                },
            ],
            activityResponses: this.activityResponsesInput,
            gradeTranslator: new GradeTranslator(this.ranges),
            showTool: false,
            scoresInline: '',
            search: '',
            sort: 'last_names',
            sortOptions: [
                {
                    name: this.$t('messages.last_names'),
                    value: 'last_names',
                },
                {
                    name: this.$t('messages.first_name'),
                    value: 'first_name',
                },
            ],
        };
    },
    props: {
        activityResponsesInput: {
            type: Array,
            required: true,
        },
        activity: {
            type: Object,
            required: true,
        },
        ranges: {
            type: Array,
            required: true,
        },
        absent: {
            type: Array,
            required: true,
        },
        barChartOptions: {
            type: Object,
            required: true,
        },
        lineChartOptions: {
            type: Object,
            required: true,
        },
    },
    methods: {
        shouldDistinguish(id) {
            if (this.show === 'all') {
                return false;
            }

            return this.absent.includes(+id);
        },
        translateToDecimal(score) {
            if (!score) {
                return '';
            }

            return ((score / this.activity.max_score) * 10).toFixed(2);
        },
        assignScores() {
            for (let i = 0; i < this.totalStudents; i++) {
                let score = this.scoresFromLine[i];
                this.activityResponses[i].score = score;
                this.activityResponses[i].decimal_score = score;
                this.activityResponses[i].absent = score === null || score === '';
            }
            this.scoresInline = '';
        },
    },
    computed: {
        counters: function () {
            if (this.gradeTranslator === undefined) {
                return [];
            }

            const counters = this.gradeTranslator.getCounterMap();

            this.scoredStudents.forEach((s) => {
                let key = this.gradeTranslator.scoreToLetter((s.score / this.activity.max_score) * 10);
                if (key) {
                    let index = counters.findIndex((c) => c.key === key);
                    counters[index].value++;
                }
            });

            return counters;
        },
        searchedUserIds: function () {
            if (this.search.trim() === '' && !(this.show === 'hide_absent_students')) {
                return this.activityResponses.map((r) => r.student_id);
            }

            return this.activityResponses
                .filter((r) => {
                    const fullName = r.student.last_name + r.student.second_last_name + r.student.name_or_nickname;

                    const searchStringWithoutAccents = deburr(this.search.trim().toUpperCase());
                    const fullNameWithoutAccents = deburr(fullName.toUpperCase());

                    return fullNameWithoutAccents.indexOf(searchStringWithoutAccents) !== -1;
                })
                .map((r) => r.student_id)
                .filter((i) => {
                    if (this.show !== 'hide_absent_students') {
                        return true;
                    }

                    return !this.absent.includes(+i);
                });
        },
        filteredResponses: function () {
            return this.activityResponses.filter((r) => {
                return this.searchedUserIds.includes(r.student_id);
            });
        },
        scoresFromLine: function () {
            if (!this.scoresInline) {
                return [];
            }

            return this.scoresInline
                .split(' ')
                .map((x) => (x === '' ? null : parseFloat(x)))
                .filter((x) => !Number.isNaN(x) || x === null);
        },
        scoredStudents: function () {
            return this.activityResponses.filter(
                (response) => response.score !== null && !isNaN(response.score) && response.score !== '',
            );
        },
        decimalScores: function () {
            return this.scoredStudents.map((s) => (s.score / this.activity.max_score) * 10);
        },
        scoredStudentsScores: function () {
            return this.scoredStudents.map((r) => parseFloat(r.score));
        },
        failedStudents: function () {
            return this.scoredStudentsScores.filter((response) => response < this.activity.max_score / 2);
        },
        totalStudents: function () {
            return this.activityResponses.length;
        },
        missed: function () {
            return this.totalStudents - this.scoredStudents.length;
        },
        passed: function () {
            return this.totalStudents - this.failedStudents.length - this.missed;
        },
        failed: function () {
            return this.failedStudents.length;
        },
        signedStudents: function () {
            return this.activityResponses.filter((response) => response.signed);
        },
        averageScore: function () {
            const activity = this.activity;

            if (this.scoredStudents.length === 0) {
                return '';
            }

            const total = this.scoredStudents.reduce(function (accumulator, currentValue) {
                const score = parseFloat(currentValue.score);
                return accumulator + Math.round((10 * score) / activity.max_score);
            }, 0);
            const average = total / this.scoredStudents.length;
            return average.toLocaleString(undefined, {
                maximumFractionDigits: 2,
            });
        },
        averageScorePill: function () {
            const [bgColor, textColor] = this.gradeTranslator.scoreToPillColors(this.averageScore);
            return {
                decimal_score: this.averageScore,
                classes: bgColor + ' ' + textColor,
            };
        },
        minScore: function () {
            return Math.min(...this.scoredStudentsScores);
        },
        minScorePill: function () {
            const decimalScore = (this.minScore / this.activity.max_score) * 10;
            const [bgColor, textColor] = this.gradeTranslator.scoreToPillColors(decimalScore);
            return {
                score: this.minScore,
                decimal_score: decimalScore.toPrecision(3),
                classes: bgColor + ' ' + textColor,
            };
        },
        topScore: function () {
            return Math.max(...this.scoredStudentsScores);
        },
        topScorePill: function () {
            const decimalScore = (this.topScore / this.activity.max_score) * 10;
            const [bgColor, textColor] = this.gradeTranslator.scoreToPillColors(decimalScore);
            return {
                score: this.topScore,
                decimal_score: decimalScore.toPrecision(3),
                classes: bgColor + ' ' + textColor,
            };
        },
    },
    created() {
        // this.gradeTranslator = new GradeTranslator(this.ranges);
    },
    mounted() {
        this.$forceUpdate();
    },
    watch: {
        sort: {
            immediate: true,
            handler: function (newSort) {
                if (newSort === 'last_names') {
                    this.activityResponses.sort((a, b) => {
                        const aName = a.student.last_name + a.student.second_last_name + a.student.name_or_nickname;
                        const bName = b.student.last_name + b.student.second_last_name + b.student.name_or_nickname;
                        return aName.localeCompare(bName);
                    });
                } else {
                    this.activityResponses.sort((a, b) => {
                        const aName = a.student.name_or_nickname + a.student.last_name + a.student.second_last_name;
                        const bName = b.student.name_or_nickname + b.student.last_name + b.student.second_last_name;
                        return aName.localeCompare(bName);
                    });
                }
            },
        },
    },
};
</script>
